import * as React from "react";
import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { Divider, ListItemAvatarClassKey, Stack } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import ErrorHandler from "../list-manager/error-handling/ErrorHandler";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { AxiosError } from "axios";
import LayersIcon from "@mui/icons-material/Layers";
// import { useListsWithAgents, useListPriorityMutation } from "./api/lists";
// import ListWithAgentsItem from "./components/ListWithAgentsItem";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import CheckIcon from "@mui/icons-material/Check";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AgentsList from "./components/AgentsList";
import AgentListPriority from "./components/AgentListPriority";
import { useListPriorityMutation } from "./api";

type DragListData = ListWithAgents;

dayjs.extend(utc);

type CreateListFormData = {
  name: string;
  // end_utc?: Date | null;
  start_utc?: Date | null;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialog-paper": {
    width: "auto", // Let it grow with content
    maxWidth: "90vw", // Prevent it from overflowing viewport
    overflowX: "auto", // Add scroll if it still overflows
  },
}));

export default function AgentPriorityModal() {
  const [open, setOpen] = React.useState(false);
  const [newListsOrder, setNewListsOrder] = useState<List[]>([]);
  const [oldListsOrder, setOldListsOrder] = useState<List[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<
    Agent | null | undefined
  >();

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
    error: saveError,
  } = useListPriorityMutation();


  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    resetMutation();
    setNewListsOrder([]);
  };

  const onSubmit = () => {
    const body = newListsOrder.map((list) => list.id);
    mutate({order: body});
  };

  const hasChanges = React.useMemo(() => {
    if (newListsOrder.length !== oldListsOrder.length) return true;
    return newListsOrder.some((list, idx) => list.id !== oldListsOrder[idx].id);
  }, [newListsOrder, oldListsOrder]);

  const onSelectAgent = (agent: Agent) => {
    resetMutation();
    if(agent.id === selectedAgent?.id) return setSelectedAgent(undefined);
    return setSelectedAgent(agent);
  };

  useEffect(() => {
    resetMutation();
  },[newListsOrder])

  return (
    <React.Fragment>
      <IconButton aria-label="add" onClick={handleClickOpen}>
        <SupportAgentIcon />
      </IconButton>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <SupportAgentIcon style={{ color: "gray" }} />
            <Typography style={{ fontSize: 22 }}>
              Agent List Priority
            </Typography>
          </Stack>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          <Stack paddingLeft={1} height="50vh">
            <Typography
              style={{
                paddingBottom: 3,
                fontSize: 14,
                color: "gray",
                fontWeight: "bold",
              }}
            >
              Select an Agent and Change List Priority
            </Typography>
            <Divider style={{ height: 2 }} />
            <Stack flexDirection="row" height="100%">
              <AgentsList
                selectedAgent={selectedAgent}
                onSelectAgent={onSelectAgent}
              />
              <span
                style={{ width: 1, height: "100%", background: "lightgray" }}
              />
              <AgentListPriority
                selectedAgent={selectedAgent}
                newListsOrder={newListsOrder}
                setNewListsOrder={setNewListsOrder}
                oldListsOrder={oldListsOrder}
                setOldListsOrder={setOldListsOrder}
                isPending={isPending}
                isSuccess={isSuccess}
                saveError={saveError as AxiosError}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            autoFocus
            onClick={onSubmit}
            disabled={isPending || !hasChanges}
          >
            Save List Priority
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
}
